<div class="wizard-layout">

  <!-- LEFT: stepper + step content + footer -->
  <div class="wizard-left-pane">
    <wizard-stepper
      [totalSteps]="totalSteps"
      [currentStep]="state.step"
      [furthestStepReached]="state.furthestStepReached"
      [canGoForward]="!isLastStep && canGoNext"
      (stepClicked)="goToStep($event)">
    </wizard-stepper>

    <div class="wizard-step-body">
      <!-- Step 1: what to create -->
      <wizard-step-create-type *ngIf="state.step === 1"
        [selectedType]="state.creationType"
        (selectedTypeChange)="onTypeSelected($event)">
      </wizard-step-create-type>

      <!-- Steps 2-5: Chart Readings flow (drives the preview even at step 1) -->
      <chart-favorite-wizard-flow *ngIf="isChartFlow"
        [step]="state.step"
        [state]="state"
        [dashboards]="dashboards"
        [associatedUserGroups]="associatedUserGroups"
        [username]="username"
        [editFavorite]="editFavorite"
        [isEditMode]="isEditMode"
        (stepValidChange)="onStepValidChange($event)"
        (previewChanged)="onPreviewChanged($event)">
      </chart-favorite-wizard-flow>

      <!-- Steps 2-7: Notification & Action flow -->
      <notification-wizard-flow *ngIf="isNotificationFlow"
        [step]="state.step"
        [state]="state"
        [dashboards]="dashboards"
        [associatedUserGroups]="associatedUserGroups"
        [username]="username"
        [gatewayNames]="editorGatewayNames"
        [devices]="editorDevices"
        (stepValidChange)="onStepValidChange($event)">
      </notification-wizard-flow>

      <!-- Steps 2-5: Image Favorite flow -->
      <image-favorite-wizard-flow *ngIf="isImageFlow"
        [step]="state.step"
        [state]="state"
        [dashboards]="dashboards"
        [associatedUserGroups]="associatedUserGroups"
        [username]="username"
        [gateways]="gateways"
        [editImageFavorite]="editImageFavorite"
        [isEditMode]="isEditMode"
        (stepValidChange)="onStepValidChange($event)">
      </image-favorite-wizard-flow>

      <!-- Steps 2-4: Map Favorite flow -->
      <map-favorite-wizard-flow *ngIf="isMapFlow"
        [step]="state.step"
        [state]="state"
        [dashboards]="dashboards"
        [associatedUserGroups]="associatedUserGroups"
        [username]="username"
        [editMapFavorite]="editMapFavorite"
        [isEditMode]="isEditMode"
        (stepValidChange)="onStepValidChange($event)"
        (previewChanged)="onMapPreviewChanged($event)">
      </map-favorite-wizard-flow>
    </div>

    <div class="wizard-footer">
      <button type="button" class="btn btn-basic" (click)="cancel()">Cancel</button>
      <button type="button" class="btn btn-success" *ngIf="!isLastStep" [disabled]="!canGoNext" (click)="next()">Next</button>
      <button type="button" class="btn btn-success" *ngIf="isLastStep" [disabled]="!canGoNext || saving" (click)="finish()">Finish</button>
    </div>
  </div>

  <!-- RIGHT: live preview of what is being built -->
  <div class="wizard-right-pane">
    <ng-container *ngIf="isChartFlow">
      <div class="wizard-preview-header">Chart Preview</div>
      <!-- Editing a saved favorite: its labels show and can be edited (saved immediately, as on Analyze Sensor Readings). -->
      <chart-readings-echart-modal *ngIf="preview.sensorReadings.length > 0"
        #previewEchart
        [sensors]="preview.sensors"
        [sensorReadings]="preview.sensorReadings"
        [sensorReadingTypes]="preview.sensorReadingTypes"
        [sensorGroups]="preview.sensorGroups"
        [dateRangeStart]="preview.dateRangeStart"
        [includeCalValues]="preview.includeCalValues"
        [includeOrigValues]="preview.includeOrigValues"
        [darkBackground]="preview.darkBackground"
        [savedChartDetails]="preview.savedChartDetails"
        [chartFavoriteId]="editFavorite?.id || 0"
        [labelsEditable]="editFavorite != null"
        [showYAxisSlider]="true">
      </chart-readings-echart-modal>
      <div class="preview-empty" *ngIf="preview.sensorReadings.length === 0">
        Select sensors and readings to preview the chart.
      </div>
    </ng-container>

    <ng-container *ngIf="isNotificationFlow">
      <div class="wizard-preview-header">Notifications (Events &amp; Actions)</div>
      <app-edit-events-and-actions-modal
        [embedded]="true"
        [event]="state.notification.event"
        [events]="notificationEditorEvents"
        [sensorGroups]="editorSensorGroups"
        [sensorDataTypes]="state.notification.availableReadingTypes"
        [evaluateTimes]="editorEvaluateTimes"
        [devices]="editorDevices"
        [IrrDestinationDDOptions]="editorGatewayNames"
        [CamGatewayDDOptions]="editorGatewayNames"
        [IrrZoneDDOptions]="editorIrrZones"
        [globalWaterSchedules]="notificationEditorSchedules"
        [actionQueues]="notificationEditorActionQueues"
        [associatedUserGroups]="associatedUserGroups"
        [userGroups]="associatedUserGroups"
        [selectedUserGroup]="state.notification.accessUserGroup"
        [username]="username"
        [eventTitle]="state.notification.event.name"
        [buttonValue]="'Save'">
      </app-edit-events-and-actions-modal>
    </ng-container>

    <ng-container *ngIf="isImageFlow">
      <div class="wizard-preview-header">Image Favorite Preview</div>
      <kig-image-favorite
        size="Large"
        [imageFavorite]="imagePreviewFavorite"
        [draftImageSrc]="state.imageFavorite.previewImageSrc"
        [draftLabels]="state.imageFavorite.labels"
        (draftLabelsChange)="onImageLabelsChange($event)"
        [labelsEditable]="state.step === 4"
        [username]="username">
      </kig-image-favorite>
      <div class="hint" *ngIf="state.step === 4" style="margin-top: 6px;">
        Use the Labels button to add labels, then drag them into place.
      </div>
    </ng-container>

    <ng-container *ngIf="isMapFlow">
      <div class="wizard-preview-header">Map Favorite Preview</div>
      <!-- Always shown (not just on the arrange step) so the Save step can read its live view. Labels are
           drafts held in wizard state and are edited here on step 3. -->
      <kig-map-favorite #previewMap
        [height]="'calc(92vh - 110px)'"
        [sensors]="mapPreview.sensors"
        [sensorReadings]="mapPreview.sensorReadings"
        [savedMapDetails]="mapPreview.savedMapDetails"
        [draftLabels]="state.mapFavorite.labels"
        (draftLabelsChange)="onMapLabelsChange($event)"
        (mapDetailsChanged)="onMapDetailsChanged($event)"
        [labelsEditable]="state.step === 3"
        [username]="username">
      </kig-map-favorite>
      <div class="hint" *ngIf="state.step === 3" style="margin-top: 6px;">
        Open and drag pin popups, and use the Labels button to add labels, then drag them into place.
      </div>
    </ng-container>

    <div class="preview-empty" *ngIf="!isChartFlow && !isNotificationFlow && !isImageFlow && !isMapFlow">
      Select what to create to see a preview.
    </div>
  </div>

</div>

<p-confirmDialog key="wizardCancel" #wizardCancelConfirm>
  <ng-template #footer>
    <button type="button" class="btn btn-basic" (click)="wizardCancelConfirm.onReject()">No</button>
    <button type="button" class="btn btn-success" (click)="wizardCancelConfirm.onAccept()">Yes</button>
  </ng-template>
</p-confirmDialog>
